/**
 * Painel da plataforma: assinaturas dos donos de grupo.
 *
 * A pergunta que a tela responde primeiro é "quem me deve e quem mandou
 * comprovante" — por isso a fila de conferência abre a lista, e a
 * configuração de preço fica recolhida no fim.
 */

import type {
  BillingState,
  PaymentMethod,
  PlatformBillingGroupRow,
  PlatformInvoice,
} from '@nossorachao/types';
import { formatCurrency, monthLabel, toCents } from '@nossorachao/utils';
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  Lock,
  MessageCircle,
  Receipt,
  Search,
  Settings,
  Wallet,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { PageHeader } from '@/components/layout';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  ErrorState,
  Field,
  Input,
  ProgressBar,
  Skeleton,
  StatTile,
  Tabs,
  cx,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import {
  openPlatformReceipt,
  useGeneratePlatformInvoices,
  usePlatformBilling,
  usePlatformInvoices,
  useReviewPlatformInvoice,
  useUpdateGroupBilling,
  useUpdatePlatformSettings,
} from '@/lib/queries';

import { BILLING_STATE_LABEL, INVOICE_STATUS_LABEL, formatDay } from './group-billing';
import { Modal } from './groups';

type Filter = 'all' | 'review' | 'late' | 'blocked' | 'ok' | 'exempt';

const FILTERS: Record<Filter, (row: PlatformBillingGroupRow) => boolean> = {
  all: () => true,
  review: (row) => row.openInvoice?.status === 'IN_REVIEW' || row.state === 'IN_REVIEW',
  late: (row) => row.state === 'OVERDUE' || row.state === 'BLOCKED',
  blocked: (row) => row.state === 'BLOCKED',
  ok: (row) => row.state === 'OK' || row.state === 'DUE',
  exempt: (row) => row.state === 'EXEMPT' || row.state === 'DISABLED',
};

/** Quem precisa de ação primeiro. */
const STATE_ORDER: Record<BillingState, number> = {
  IN_REVIEW: 0,
  BLOCKED: 1,
  OVERDUE: 2,
  DUE: 3,
  OK: 4,
  EXEMPT: 5,
  DISABLED: 6,
};

const METHOD_LABEL: Record<PaymentMethod, string> = {
  PIX: 'Pix',
  TRANSFER: 'Transferência',
  CASH: 'Dinheiro',
  CARD: 'Cartão',
  OTHER: 'Outro',
};

export function PlatformBillingPage() {
  const overview = usePlatformBilling();
  const generate = useGeneratePlatformInvoices();

  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<PlatformBillingGroupRow | null>(null);
  const [review, setReview] = useState<{ invoice: PlatformInvoice; groupName: string } | null>(
    null,
  );
  const [showSettings, setShowSettings] = useState(false);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (overview.data?.groups ?? [])
      .filter(FILTERS[filter])
      .filter(
        (row) =>
          !term ||
          row.name.toLowerCase().includes(term) ||
          row.owner.name.toLowerCase().includes(term) ||
          row.owner.email.toLowerCase().includes(term),
      )
      .sort((a, b) => STATE_ORDER[a.state] - STATE_ORDER[b.state] || a.name.localeCompare(b.name));
  }, [overview.data, filter, search]);

  if (overview.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!overview.data) {
    return (
      <ErrorState
        message={
          overview.error instanceof ApiError ? overview.error.message : 'Erro ao carregar o painel.'
        }
        onRetry={() => void overview.refetch()}
      />
    );
  }

  const { totals, settings, referenceMonth, series } = overview.data;
  const all = overview.data.groups;
  const disabled = settings.monthlyPriceCents === 0;

  return (
    <>
      <PageHeader
        title="Assinaturas"
        subtitle={`Cobrança da plataforma aos donos de grupo · ${monthLabel(referenceMonth)}`}
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" icon={Settings} onClick={() => setShowSettings(true)}>
              Configurar
            </Button>
            <Button
              icon={Receipt}
              loading={generate.isPending}
              disabled={disabled}
              onClick={() => generate.mutate({ referenceMonth })}
            >
              Gerar faturas do mês
            </Button>
          </div>
        }
      />

      {disabled ? (
        <Card className="mb-6" accent="rgb(21 112 239)">
          <p className="font-semibold">Cobrança desligada</p>
          <p className="mt-0.5 text-sm text-ink-2">
            O valor padrão está em R$ 0,00, então nenhuma fatura é gerada. Defina o valor em
            Configurar — grupos com valor especial continuam sendo cobrados.
          </p>
        </Card>
      ) : null}

      {!settings.pixKeyMasked && !disabled ? (
        <Card className="mb-6" accent="rgb(220 104 3)">
          <p className="font-semibold">Chave Pix não configurada</p>
          <p className="mt-0.5 text-sm text-ink-2">
            Sem ela os donos não conseguem gerar o QR Code para pagar.
          </p>
        </Card>
      ) : null}

      {generate.data ? (
        <p className="mb-4 text-sm text-ink-2">
          {generate.data.created > 0
            ? `${generate.data.created} fatura(s) gerada(s), ${formatCurrency(generate.data.totalCents)} no total.`
            : 'Nenhuma fatura nova — todos os grupos cobráveis já têm fatura neste mês.'}
        </p>
      ) : null}

      {/* ── Totais ──────────────────────────────────────────────────────── */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card p-5">
          <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-ink-3">
            <Wallet className="h-3.5 w-3.5" aria-hidden />
            Recebido no mês
          </p>
          <p className="tabular mt-1 text-2xl font-extrabold text-ok">
            {formatCurrency(totals.receivedCents)}
          </p>
          <div className="mt-2">
            <ProgressBar value={totals.receivedCents} max={totals.expectedCents} tone="ok" />
          </div>
          <p className="mt-1.5 text-[13px] text-ink-3">
            de {formatCurrency(totals.expectedCents)} faturados
          </p>
        </div>
        <StatTile
          label="Em aberto"
          value={formatCurrency(totals.outstandingCents)}
          hint="todas as competências"
          tone={totals.outstandingCents > 0 ? 'warn' : 'default'}
          onClick={() => setFilter('late')}
        />
        <StatTile
          label="Comprovantes"
          value={String(totals.inReview)}
          hint="aguardando conferência"
          tone={totals.inReview > 0 ? 'warn' : 'default'}
          icon={FileText}
          onClick={() => setFilter('review')}
        />
        <StatTile
          label="Bloqueados"
          value={String(totals.blocked)}
          hint={`${totals.overdue} vencidos na carência · ${totals.billable} cobráveis`}
          tone={totals.blocked > 0 ? 'bad' : 'default'}
          icon={Lock}
          onClick={() => setFilter('blocked')}
        />
      </div>

      <SeriesCard series={series} />

      {/* ── Grupos ──────────────────────────────────────────────────────── */}
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <Tabs<Filter>
          active={filter}
          onChange={setFilter}
          tabs={[
            { key: 'all', label: 'Todos', count: all.length },
            { key: 'review', label: 'Conferir', count: all.filter(FILTERS.review).length },
            { key: 'late', label: 'Atrasados', count: all.filter(FILTERS.late).length },
            { key: 'blocked', label: 'Bloqueados', count: all.filter(FILTERS.blocked).length },
            { key: 'ok', label: 'Em dia', count: all.filter(FILTERS.ok).length },
            { key: 'exempt', label: 'Sem cobrança', count: all.filter(FILTERS.exempt).length },
          ]}
        />
        <div className="relative lg:w-72">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3"
            aria-hidden
          />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Grupo, dono ou e-mail"
            className="pl-9"
            aria-label="Buscar grupo"
          />
        </div>
      </div>

      {rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={CheckCircle2}
            title="Nada por aqui"
            description="Nenhum grupo neste filtro."
          />
        </Card>
      ) : (
        <Card className="p-0">
          <ul className="divide-y divide-line">
            {rows.map((row) => (
              <GroupRow
                key={row.groupId}
                row={row}
                onOpen={() => setSelected(row)}
                onReview={(invoice) => setReview({ invoice, groupName: row.name })}
              />
            ))}
          </ul>
        </Card>
      )}

      {selected ? (
        <GroupModal
          row={all.find((row) => row.groupId === selected.groupId) ?? selected}
          referenceMonth={referenceMonth}
          onReview={(invoice) => setReview({ invoice, groupName: selected.name })}
          onClose={() => setSelected(null)}
        />
      ) : null}

      {review ? (
        <ReviewModal
          invoice={review.invoice}
          groupName={review.groupName}
          onClose={() => setReview(null)}
        />
      ) : null}

      {showSettings ? <SettingsModal onClose={() => setShowSettings(false)} /> : null}
    </>
  );
}

// =============================================================================
// Linha do grupo
// =============================================================================

function GroupRow({
  row,
  onOpen,
  onReview,
}: {
  row: PlatformBillingGroupRow;
  onOpen: () => void;
  onReview: (invoice: PlatformInvoice) => void;
}) {
  const state = BILLING_STATE_LABEL[row.state];
  const invoice = row.openInvoice;
  const actionable = invoice && (invoice.status === 'PENDING' || invoice.status === 'IN_REVIEW');

  return (
    <li className="flex flex-col gap-3 p-4 lg:flex-row lg:items-center">
      <button type="button" onClick={onOpen} className="min-w-0 flex-1 text-left">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate font-semibold hover:text-brand">{row.name}</span>
          <Badge tone={state.tone}>{state.label}</Badge>
          {row.priceOverrideCents !== null && !row.exempt ? (
            <Badge tone="brand">Valor especial</Badge>
          ) : null}
        </div>
        <p className="mt-0.5 truncate text-[13px] text-ink-3">
          {row.owner.name} · {row.owner.email} · {row.memberCount} participantes
        </p>
      </button>

      <div className="flex items-center gap-4 lg:w-[360px] lg:justify-end">
        <div className="min-w-0 flex-1 text-[13px] lg:text-right">
          {invoice ? (
            <>
              <p className="tabular font-semibold">
                {formatCurrency(invoice.amountCents)} · {monthLabel(invoice.referenceMonth)}
              </p>
              <p className={cx('text-ink-3', row.state === 'BLOCKED' && 'text-bad')}>
                {invoice.status === 'PAID'
                  ? `paga em ${formatDay(invoice.paidAt ?? invoice.dueDate)}`
                  : row.state === 'BLOCKED'
                    ? `bloqueado desde ${formatDay(row.blocksAt ?? invoice.dueDate)}`
                    : `vence ${formatDay(invoice.dueDate)}`}
              </p>
            </>
          ) : (
            <p className="text-ink-3">
              {row.exempt
                ? 'Isento'
                : row.lastPaidAt
                  ? `Último pagamento ${formatDay(row.lastPaidAt)}`
                  : 'Sem fatura'}
            </p>
          )}
        </div>

        {actionable ? (
          <Button
            size="sm"
            variant={invoice.status === 'IN_REVIEW' ? 'primary' : 'secondary'}
            onClick={() => onReview(invoice)}
          >
            {invoice.status === 'IN_REVIEW' ? 'Conferir' : 'Dar baixa'}
          </Button>
        ) : null}
      </div>
    </li>
  );
}

// =============================================================================
// Série mensal
// =============================================================================

function SeriesCard({
  series,
}: {
  series: Array<{ referenceMonth: string; expectedCents: number; receivedCents: number }>;
}) {
  const peak = Math.max(1, ...series.map((point) => point.expectedCents));
  if (series.every((point) => point.expectedCents === 0)) return null;

  return (
    <Card className="mb-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-bold">Últimos meses</h2>
        <div className="flex gap-4 text-[12px] text-ink-3">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-line-strong" aria-hidden /> Faturado
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-ok" aria-hidden /> Recebido
          </span>
        </div>
      </div>
      <ul className="space-y-3">
        {series.map((point) => (
          <li
            key={point.referenceMonth}
            className="grid grid-cols-[88px_1fr_auto] items-center gap-3"
          >
            <span className="text-[13px] font-semibold text-ink-2">
              {monthLabel(point.referenceMonth)}
            </span>
            <div className="relative h-2.5 overflow-hidden rounded-pill bg-sunken">
              <div
                className="absolute inset-y-0 left-0 rounded-pill bg-line-strong"
                style={{ width: `${(point.expectedCents / peak) * 100}%` }}
              />
              <div
                className="absolute inset-y-0 left-0 rounded-pill bg-ok"
                style={{ width: `${(point.receivedCents / peak) * 100}%` }}
              />
            </div>
            <span className="tabular text-right text-[13px] text-ink-2">
              {formatCurrency(point.receivedCents)}
              <span className="text-ink-3"> / {formatCurrency(point.expectedCents)}</span>
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// =============================================================================
// Conferência de uma fatura
// =============================================================================

function ReviewModal({
  invoice,
  groupName,
  onClose,
}: {
  invoice: PlatformInvoice;
  groupName: string;
  onClose: () => void;
}) {
  const review = useReviewPlatformInvoice();
  const [method, setMethod] = useState<PaymentMethod>('PIX');
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState<'confirm' | 'reject' | 'waive'>('confirm');

  const canReject = invoice.status === 'IN_REVIEW' || invoice.status === 'PAID';
  const needsReason = mode !== 'confirm';

  const submit = () =>
    review.mutate(
      mode === 'confirm'
        ? { invoiceId: invoice.id, action: 'confirm', method, notes: reason || undefined }
        : { invoiceId: invoice.id, action: mode, reason },
      { onSuccess: onClose },
    );

  return (
    <Modal title={`${groupName} · ${monthLabel(invoice.referenceMonth)}`} onClose={onClose}>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="tabular text-2xl font-extrabold">{formatCurrency(invoice.amountCents)}</p>
          <Badge tone={INVOICE_STATUS_LABEL[invoice.status].tone}>
            {INVOICE_STATUS_LABEL[invoice.status].label}
          </Badge>
        </div>

        <p className="text-[13px] text-ink-3">
          Vence {formatDay(invoice.dueDate)}
          {invoice.receiptSentAt
            ? ` · comprovante enviado em ${formatDay(invoice.receiptSentAt)}`
            : ''}
        </p>

        {invoice.hasReceipt ? (
          <Button
            variant="secondary"
            icon={FileText}
            full
            onClick={() => void openPlatformReceipt(invoice.id)}
          >
            Ver comprovante
          </Button>
        ) : null}

        <Tabs<'confirm' | 'reject' | 'waive'>
          active={mode}
          onChange={setMode}
          tabs={[
            { key: 'confirm', label: 'Dar baixa' },
            ...(canReject ? [{ key: 'reject' as const, label: 'Recusar' }] : []),
            { key: 'waive', label: 'Isentar' },
          ]}
        />

        {mode === 'confirm' ? (
          <Field label="Forma de pagamento">
            <select
              className="input"
              value={method}
              onChange={(event) => setMethod(event.target.value as PaymentMethod)}
            >
              {Object.entries(METHOD_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        <Field
          label={needsReason ? 'Motivo' : 'Observação (opcional)'}
          hint={
            mode === 'reject'
              ? 'O dono vê este motivo. A fatura volta a ficar em aberto.'
              : mode === 'waive'
                ? 'A fatura deixa de ser cobrada.'
                : undefined
          }
        >
          <Input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            maxLength={200}
            placeholder={mode === 'reject' ? 'Valor não entrou na conta' : ''}
          />
        </Field>

        {review.error ? (
          <p className="text-[13px] text-bad">
            {review.error instanceof ApiError ? review.error.message : 'Erro ao salvar.'}
          </p>
        ) : null}

        <Button
          full
          variant={mode === 'confirm' ? 'success' : mode === 'reject' ? 'danger' : 'primary'}
          loading={review.isPending}
          disabled={needsReason && reason.trim().length < 3}
          onClick={submit}
        >
          {mode === 'confirm'
            ? 'Confirmar pagamento'
            : mode === 'reject'
              ? 'Recusar e reabrir'
              : 'Isentar fatura'}
        </Button>
      </div>
    </Modal>
  );
}

// =============================================================================
// Detalhe do grupo: condição comercial e histórico
// =============================================================================

function GroupModal({
  row,
  referenceMonth,
  onReview,
  onClose,
}: {
  row: PlatformBillingGroupRow;
  referenceMonth: string;
  onReview: (invoice: PlatformInvoice) => void;
  onClose: () => void;
}) {
  const update = useUpdateGroupBilling();
  const generate = useGeneratePlatformInvoices();
  const history = usePlatformInvoices({ groupId: row.groupId });

  const [price, setPrice] = useState(
    row.priceOverrideCents !== null
      ? (row.priceOverrideCents / 100).toFixed(2).replace('.', ',')
      : '',
  );
  const [exempt, setExempt] = useState(row.exempt);
  const [notes, setNotes] = useState(row.notes ?? '');
  const [priceError, setPriceError] = useState<string | null>(null);

  const save = () => {
    let priceOverrideCents: number | null = null;
    if (price.trim()) {
      try {
        priceOverrideCents = toCents(price);
      } catch {
        setPriceError('Valor inválido');
        return;
      }
    }
    setPriceError(null);
    update.mutate({ groupId: row.groupId, priceOverrideCents, exempt, notes: notes || null });
  };

  const whatsapp = row.owner.phone ? `https://wa.me/${row.owner.phone}` : null;
  const hasCurrent = history.data?.some((invoice) => invoice.referenceMonth === referenceMonth);

  return (
    <Modal title={row.name} onClose={onClose}>
      <div className="max-h-[70vh] space-y-5 overflow-y-auto pr-1">
        <div className="text-[13px] text-ink-2">
          <p className="font-semibold text-ink">{row.owner.name}</p>
          <p>{row.owner.email}</p>
          <p className="text-ink-3">
            {row.memberCount} participantes · grupo criado em {formatDay(row.createdAt)}
          </p>
          {whatsapp ? (
            <a
              href={whatsapp}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 font-semibold text-brand"
            >
              <MessageCircle className="h-4 w-4" aria-hidden /> Falar no WhatsApp
            </a>
          ) : null}
        </div>

        <div className="space-y-3 border-t border-line pt-4">
          <Field
            label="Valor especial (R$/mês)"
            error={priceError ?? undefined}
            hint="Vazio = valor padrão. Vale para as próximas faturas."
          >
            <Input
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              inputMode="decimal"
              placeholder="Padrão"
            />
          </Field>
          <Checkbox
            checked={exempt}
            onChange={setExempt}
            label="Isento — não gera fatura nem bloqueia"
          />
          <Field label="Observação interna">
            <Input
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              maxLength={200}
            />
          </Field>
          {update.error ? (
            <p className="text-[13px] text-bad">
              {update.error instanceof ApiError ? update.error.message : 'Erro ao salvar.'}
            </p>
          ) : null}
          <Button full loading={update.isPending} onClick={save}>
            {update.isSuccess ? 'Salvo' : 'Salvar condição'}
          </Button>
        </div>

        <div className="border-t border-line pt-4">
          <div className="mb-2 flex items-center justify-between gap-2">
            <h3 className="font-bold">Faturas</h3>
            {!hasCurrent && !row.exempt ? (
              <Button
                size="sm"
                variant="ghost"
                loading={generate.isPending}
                onClick={() =>
                  generate.mutate(
                    { referenceMonth, groupId: row.groupId },
                    { onSuccess: () => void history.refetch() },
                  )
                }
              >
                Gerar {monthLabel(referenceMonth)}
              </Button>
            ) : null}
          </div>

          {history.isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : !history.data?.length ? (
            <p className="text-[13px] text-ink-3">Nenhuma fatura ainda.</p>
          ) : (
            <ul className="divide-y divide-line">
              {history.data.map((invoice) => (
                <li key={invoice.id} className="flex items-center gap-2 py-2.5">
                  <div className="min-w-0 flex-1 text-[13px]">
                    <p className="font-semibold">
                      {monthLabel(invoice.referenceMonth)} · {formatCurrency(invoice.amountCents)}
                    </p>
                    <p className="truncate text-ink-3">
                      {invoice.paidAt
                        ? `paga em ${formatDay(invoice.paidAt)}${invoice.method ? ` · ${METHOD_LABEL[invoice.method]}` : ''}`
                        : `vence ${formatDay(invoice.dueDate)}`}
                      {invoice.statusReason ? ` · ${invoice.statusReason}` : ''}
                    </p>
                  </div>
                  <Badge tone={INVOICE_STATUS_LABEL[invoice.status].tone}>
                    {INVOICE_STATUS_LABEL[invoice.status].label}
                  </Badge>
                  {invoice.status !== 'WAIVED' && invoice.status !== 'CANCELED' ? (
                    <Button size="sm" variant="ghost" onClick={() => onReview(invoice)}>
                      {invoice.status === 'PAID' ? 'Estornar' : 'Abrir'}
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}

// =============================================================================
// Configuração global
// =============================================================================

function SettingsModal({ onClose }: { onClose: () => void }) {
  const overview = usePlatformBilling();
  const update = useUpdatePlatformSettings();
  const current = overview.data?.settings;

  const [form, setForm] = useState({
    price: current ? (current.monthlyPriceCents / 100).toFixed(2).replace('.', ',') : '',
    dueDay: String(current?.dueDayOfMonth ?? 10),
    graceDays: String(current?.graceDays ?? 7),
    merchantName: current?.merchantName ?? '',
    pixKey: '',
  });
  const [error, setError] = useState<string | null>(null);

  const set = (field: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((previous) => ({ ...previous, [field]: event.target.value }));

  const save = () => {
    let monthlyPriceCents: number;
    try {
      monthlyPriceCents = form.price.trim() ? toCents(form.price) : 0;
    } catch {
      setError('Valor inválido');
      return;
    }
    setError(null);

    update.mutate(
      {
        monthlyPriceCents,
        dueDayOfMonth: Number(form.dueDay),
        graceDays: Number(form.graceDays),
        merchantName: form.merchantName,
        // Vazio mantém a chave atual: o campo nunca vem preenchido.
        ...(form.pixKey.trim() ? { pixKey: form.pixKey.trim() } : {}),
      },
      { onSuccess: onClose },
    );
  };

  const fieldErrors = update.error instanceof ApiError ? update.error.fieldErrors : {};

  return (
    <Modal title="Configurar cobrança" onClose={onClose}>
      <div className="space-y-4">
        <Field
          label="Valor padrão por grupo (R$/mês)"
          error={error ?? fieldErrors.monthlyPriceCents}
          hint="0 desliga a cobrança para quem não tem valor especial."
        >
          <Input value={form.price} onChange={set('price')} inputMode="decimal" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Dia do vencimento" error={fieldErrors.dueDayOfMonth}>
            <Input type="number" min={1} max={28} value={form.dueDay} onChange={set('dueDay')} />
          </Field>
          <Field label="Carência (dias)" error={fieldErrors.graceDays}>
            <Input
              type="number"
              min={0}
              max={60}
              value={form.graceDays}
              onChange={set('graceDays')}
            />
          </Field>
        </div>
        <Field
          label="Chave Pix da plataforma"
          hint={
            current?.pixKeyMasked
              ? `Atual: ${current.pixKeyMasked}. Deixe vazio para manter.`
              : undefined
          }
          error={fieldErrors.pixKey}
        >
          <Input
            value={form.pixKey}
            onChange={set('pixKey')}
            placeholder={
              current?.pixKeyMasked ? 'Manter a atual' : 'E-mail, CPF/CNPJ, telefone ou aleatória'
            }
            autoComplete="off"
          />
        </Field>
        <Field
          label="Nome que aparece no Pix"
          hint="Até 25 caracteres, sem acento."
          error={fieldErrors.merchantName}
        >
          <Input value={form.merchantName} onChange={set('merchantName')} maxLength={25} />
        </Field>

        <p className="flex items-start gap-2 text-[13px] text-ink-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          Mudar a carência vale na hora para todos os grupos — inclusive para quem já está vencido.
        </p>

        {update.error && !Object.keys(fieldErrors).length ? (
          <p className="text-[13px] text-bad">
            {update.error instanceof ApiError ? update.error.message : 'Erro ao salvar.'}
          </p>
        ) : null}

        <Button full loading={update.isPending} onClick={save}>
          Salvar
        </Button>
      </div>
    </Modal>
  );
}
